import { useEffect, useState } from "react";
import api, { apiError, formatEuro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ArrowDown, ArrowUp, Copy, Truck, EnvelopeSimple } from "@phosphor-icons/react";

const emptyForm = { ingredient_id: "", tipo: "carico", quantita: "", nota: "" };

export default function Magazzino() {
  const [ingredients, setIngredients] = useState([]);
  const [movements, setMovements] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [reorder, setReorder] = useState(null);
  const [qtyMap, setQtyMap] = useState({});
  const [receiving, setReceiving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [defaultEmail, setDefaultEmail] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["admin", "master"].includes(user?.role);

  const load = () => {
    api.get("/ingredients").then((r) => setIngredients(r.data)).catch(() => {});
    api.get("/warehouse/movements?limit=100").then((r) => setMovements(r.data)).catch(() => {});
    api.get("/warehouse/reorder").then((r) => {
      setReorder(r.data);
      setQtyMap(Object.fromEntries(r.data.items.map((i) => [i.id, String(i.quantita_suggerita)])));
    }).catch(() => {});
    api.get("/settings").then((r) => setDefaultEmail(r.data.fornitore_email || "")).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const rigaStima = (i) => {
    const q = parseFloat(qtyMap[i.id]) || 0;
    return i.unita === "pz" ? q * i.costo_per_unita : (q / 100) * i.costo_per_unita;
  };
  const totaleStima = (reorder?.items || []).reduce((s, i) => s + rigaStima(i), 0);

  const copiaLista = () => {
    const text = "Ordine fornitori RisoRice:\n" + reorder.items.map((i) => `- ${i.nome}: ${qtyMap[i.id] || i.quantita_suggerita} ${i.unita}`).join("\n");
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Lista copiata negli appunti"))
      .catch(() => toast.error("Impossibile copiare la lista"));
  };

  const registraArrivo = async () => {
    const items = reorder.items.map((i) => ({ ingredient_id: i.id, quantita: parseFloat(qtyMap[i.id]) || 0 })).filter((i) => i.quantita > 0);
    if (!items.length) { toast.error("Nessuna quantità da caricare"); return; }
    setReceiving(true);
    try {
      const { data } = await api.post("/warehouse/reorder/receive", { items, nota: "Ordine fornitore" });
      toast.success(data.message);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setReceiving(false); }
  };

  const apriEmail = () => {
    setEmailTo(defaultEmail);
    setSaveDefault(false);
    setEmailOpen(true);
  };

  const inviaEmail = async () => {
    if (!emailTo.trim()) { toast.error("Inserisci l'email del fornitore"); return; }
    const items = reorder.items.map((i) => ({ ingredient_id: i.id, quantita: parseFloat(qtyMap[i.id]) || 0 })).filter((i) => i.quantita > 0);
    if (!items.length) { toast.error("Nessun articolo da ordinare"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/warehouse/reorder/send-email", { recipient_email: emailTo.trim(), items });
      toast.success(data.message);
      if (saveDefault && isAdmin) {
        await api.put("/settings", { fornitore_email: emailTo.trim() });
        setDefaultEmail(emailTo.trim());
      }
      setEmailOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const save = async () => {
    if (!form.ingredient_id) { toast.error("Seleziona un ingrediente"); return; }
    const qty = parseFloat(form.quantita);
    if (!qty || qty <= 0) { toast.error("Inserisci una quantità valida"); return; }
    try {
      await api.post("/warehouse/movements", { ingredient_id: form.ingredient_id, tipo: form.tipo, quantita: qty, nota: form.nota });
      toast.success(`Movimento di ${form.tipo} registrato`);
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const stato = (i) => {
    if (i.soglia_minima > 0 && i.scorta <= i.soglia_minima)
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-full">In esaurimento</Badge>;
    if (i.soglia_minima > 0 && i.scorta <= i.soglia_minima * 1.5)
      return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-full">Basso</Badge>;
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-full">OK</Badge>;
  };

  const valore = (i) => (i.unita === "pz" ? i.scorta * i.costo_per_unita : (i.scorta / 100) * i.costo_per_unita);

  return (
    <div className="space-y-6 fade-in" data-testid="magazzino-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Magazzino</h1>
          <p className="text-[#6B7280] mt-1">Scorte, soglie minime e movimenti di carico/scarico.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="movement-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
          <Plus size={18} weight="bold" /> Nuovo movimento
        </Button>
      </div>

      <Tabs defaultValue="scorte">
        <TabsList className="h-auto bg-white border border-[#E5E5E0] rounded-full p-1">
          <TabsTrigger value="scorte" data-testid="tab-scorte" className="rounded-full px-6 py-2 data-[state=active]:bg-[#D95D39] data-[state=active]:text-white">Scorte</TabsTrigger>
          <TabsTrigger value="movimenti" data-testid="tab-movimenti" className="rounded-full px-6 py-2 data-[state=active]:bg-[#D95D39] data-[state=active]:text-white">Movimenti</TabsTrigger>
          <TabsTrigger value="ordine" data-testid="tab-ordine" className="rounded-full px-6 py-2 gap-2 data-[state=active]:bg-[#D95D39] data-[state=active]:text-white">
            Ordine fornitori
            {reorder?.items?.length > 0 && (
              <span className="bg-red-100 text-red-800 text-xs font-semibold rounded-full px-2 py-0.5" data-testid="reorder-count-badge">{reorder.items.length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scorte" className="mt-6">
          <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
            <Table data-testid="stock-table">
              <TableHeader className="bg-[#FAF9F6]">
                <TableRow>
                  <TableHead>Ingrediente</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Scorta</TableHead>
                  <TableHead>Soglia minima</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Valore</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ingredients.map((i) => (
                  <TableRow key={i.id} className="hover:bg-stone-50" data-testid={`stock-row-${i.id}`}>
                    <TableCell className="font-medium">{i.nome}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-full">{i.categoria}</Badge></TableCell>
                    <TableCell className="font-semibold">{i.scorta} {i.unita}</TableCell>
                    <TableCell className="text-[#6B7280]">{i.soglia_minima} {i.unita}</TableCell>
                    <TableCell>{stato(i)}</TableCell>
                    <TableCell>{formatEuro(valore(i))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="movimenti" className="mt-6">
          <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
            <Table data-testid="movements-table">
              <TableHeader className="bg-[#FAF9F6]">
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Ingrediente</TableHead>
                  <TableHead>Quantità</TableHead>
                  <TableHead>Nota</TableHead>
                  <TableHead>Utente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((m) => (
                  <TableRow key={m.id} className="hover:bg-stone-50" data-testid={`movement-row-${m.id}`}>
                    <TableCell className="text-sm">{new Date(m.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-full gap-1 ${m.tipo === "carico" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-orange-100 text-orange-800 hover:bg-orange-100"}`}>
                        {m.tipo === "carico" ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                        {m.tipo === "carico" ? "Carico" : "Scarico"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{m.ingrediente_nome}</TableCell>
                    <TableCell>{m.quantita} {m.unita}</TableCell>
                    <TableCell className="text-sm text-[#6B7280]">{m.nota || "—"}</TableCell>
                    <TableCell className="text-sm text-[#6B7280]">{m.utente}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="ordine" className="mt-6 space-y-4">
          {(!reorder || reorder.items.length === 0) ? (
            <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="reorder-empty">
              <p className="font-semibold">Tutte le scorte sono sopra la soglia minima.</p>
              <p className="text-sm text-[#6B7280] mt-1">Nessun ordine fornitori necessario al momento.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-[#6B7280]">Lista della spesa generata automaticamente dagli ingredienti sotto soglia minima. Quantità suggerita: riporta la scorta al doppio della soglia.</p>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" onClick={copiaLista} data-testid="reorder-copy-button" className="rounded-full gap-2 hover:bg-stone-50">
                    <Copy size={16} /> Copia lista
                  </Button>
                  <Button variant="outline" onClick={apriEmail} data-testid="reorder-email-button" className="rounded-full gap-2 hover:bg-stone-50">
                    <EnvelopeSimple size={16} /> Invia via email
                  </Button>
                  <Button onClick={registraArrivo} disabled={receiving} data-testid="reorder-receive-button" className="rounded-full bg-[#5D735D] hover:bg-[#4C5F4C] gap-2">
                    <Truck size={16} /> {receiving ? "Registrazione..." : "Registra arrivo merce"}
                  </Button>
                </div>
              </div>
              <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
                <Table data-testid="reorder-table">
                  <TableHeader className="bg-[#FAF9F6]">
                    <TableRow>
                      <TableHead>Ingrediente</TableHead>
                      <TableHead>Scorta attuale</TableHead>
                      <TableHead>Soglia minima</TableHead>
                      <TableHead>Quantità da ordinare</TableHead>
                      <TableHead>Costo stimato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reorder.items.map((i) => (
                      <TableRow key={i.id} className="hover:bg-stone-50" data-testid={`reorder-row-${i.id}`}>
                        <TableCell>
                          <p className="font-medium">{i.nome}</p>
                          <p className="text-xs text-[#6B7280]">{i.categoria}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-full">{i.scorta} {i.unita}</Badge>
                        </TableCell>
                        <TableCell className="text-[#6B7280]">{i.soglia_minima} {i.unita}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Input type="number" min="0" className="w-28" value={qtyMap[i.id] ?? ""} onChange={(e) => setQtyMap((m) => ({ ...m, [i.id]: e.target.value }))} data-testid={`reorder-qty-${i.id}`} />
                            <span className="text-sm text-[#6B7280]">{i.unita}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">{formatEuro(rigaStima(i))}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-[#FAF9F6]">
                      <TableCell colSpan={4} className="font-bold text-right">Totale stimato ordine</TableCell>
                      <TableCell className="font-extrabold text-[#D95D39]" data-testid="reorder-total">{formatEuro(totaleStima)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md" data-testid="reorder-email-dialog">
          <DialogHeader><DialogTitle>Invia ordine al fornitore</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Email fornitore</Label>
              <Input type="email" placeholder="fornitore@esempio.it" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="reorder-email-input" />
            </div>
            {isAdmin && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={saveDefault} onCheckedChange={(v) => setSaveDefault(!!v)} data-testid="reorder-email-save-default" />
                <span className="text-sm">Salva come fornitore predefinito</span>
              </label>
            )}
            <p className="text-xs text-[#6B7280]">Verrà inviata la lista di {reorder?.items?.length || 0} articoli con le quantità indicate nella tabella.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={inviaEmail} disabled={sending} data-testid="reorder-email-send-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
              <EnvelopeSimple size={16} /> {sending ? "Invio in corso..." : "Invia ordine"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="movement-dialog">
          <DialogHeader><DialogTitle>Nuovo movimento di magazzino</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Ingrediente</Label>
              <Select value={form.ingredient_id} onValueChange={(v) => setForm((f) => ({ ...f, ingredient_id: v }))}>
                <SelectTrigger data-testid="movement-ingredient-select"><SelectValue placeholder="Seleziona ingrediente" /></SelectTrigger>
                <SelectContent>
                  {ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.nome} — {i.scorta} {i.unita} disponibili</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo movimento</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carico">Carico (entrata merce)</SelectItem>
                  <SelectItem value="scarico">Scarico (uscita merce)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Quantità</Label>
              <Input type="number" step="1" value={form.quantita} onChange={(e) => setForm((f) => ({ ...f, quantita: e.target.value }))} data-testid="movement-quantity-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Nota (facoltativa)</Label>
              <Input value={form.nota} onChange={(e) => setForm((f) => ({ ...f, nota: e.target.value }))} data-testid="movement-note-input" placeholder="Es. Consegna fornitore" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="movement-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
